import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useUser } from '../../contexts/UserContext'
import type {
  DriverInvoiceItem,
  DriverInvoiceItemFields,
} from '../../types/invoice'
import type { Tag } from '../../types/tag'
import { StakeholderType } from '../../types/user'
import type { WorkOrderReportTag } from '../../types/workorder'

interface DriverInvoiceItemsTableProps {
  items: DriverInvoiceItem[]
  isEditable?: boolean
  onItemUpdate?: (
    index: number,
    field: DriverInvoiceItemFields,
    value: string | number | undefined,
  ) => void
  onItemRemove?: (index: number) => void
  onAddItem?: () => void
  tags?: (Tag | WorkOrderReportTag)[]
  tagsFiltered?: (Tag | WorkOrderReportTag)[]
  contractorNames: Record<number, string>
  brokerNames: Record<number, string>
  userTruckNumber: string
  onObjectSelect?: (index: number, objectId: number) => void
  selectedTagIds?: number[]
}

const DriverInvoiceItemsTable: React.FC<DriverInvoiceItemsTableProps> = ({
  items,
  isEditable = false,
  onItemUpdate,
  onItemRemove,
  onAddItem,
  tags = [],
  tagsFiltered = [],
  contractorNames,
  brokerNames,
  userTruckNumber,
  onObjectSelect,
  selectedTagIds = [],
}) => {
  const { user } = useUser()
  const isDriver = user?.user_type === 'DRIVER'
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
    }
  }, [])

  const scrollTable = useCallback((direction: 'left' | 'right') => {
    const container = scrollContainerRef.current
    if (container) {
      const scrollAmount = 200
      const newScrollLeft =
        direction === 'left'
          ? container.scrollLeft - scrollAmount
          : container.scrollLeft + scrollAmount
      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      })
    }
  }, [])

  useEffect(() => {
    checkScrollability()
    const timeoutId = setTimeout(checkScrollability, 100)
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollability)
      window.addEventListener('resize', checkScrollability)
      return () => {
        clearTimeout(timeoutId)
        container.removeEventListener('scroll', checkScrollability)
        window.removeEventListener('resize', checkScrollability)
      }
    }
    return () => clearTimeout(timeoutId)
  }, [checkScrollability])

  const calculateItemTotal = (item: DriverInvoiceItem) => {
    const parsed = Number.parseFloat(item.total || '0')
    return Number.isFinite(parsed) ? parsed : 0
  }

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0)
  }

  return (
    <div className={css(styles.wrapper)}>
      {canScrollLeft && (
        <>
          <div className={css(styles.scrollIndicatorLeft)} />
          <button
            type="button"
            className={css(styles.scrollArrowLeft)}
            onClick={() => scrollTable('left')}
            aria-label="Scroll left"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Scroll left</title>
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </>
      )}
      {canScrollRight && (
        <>
          <div className={css(styles.scrollIndicatorRight)} />
          <button
            type="button"
            className={css(styles.scrollArrowRight)}
            onClick={() => scrollTable('right')}
            aria-label="Scroll right"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Scroll right</title>
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </>
      )}
      <div ref={scrollContainerRef} className={css(styles.itemsTable)}>
        <table className={css(styles.table)}>
          <thead>
            <tr>
              <th className={css(styles.th, styles.tagHeader)}>Tag</th>
              <th className={css(styles.th, styles.jobSiteHeader)}>Job Site</th>
              <th className={css(styles.th, styles.truckNumberHeader)}>
                Truck #
              </th>
              <th className={css(styles.th, styles.hoursHeader)}>Hours</th>
              <th className={css(styles.th, styles.loadsHeader)}>Loads</th>
              <th className={css(styles.th, styles.dateHeader)}>Date</th>
              <th className={css(styles.th, styles.brokerContractorHeader)}>
                {isDriver ? 'Broker' : 'Contractor'}
              </th>
              <th className={css(styles.th, styles.materialHeader)}>
                Material
              </th>
              <th className={css(styles.th, styles.numberHeader)}>Weight</th>
              <th className={css(styles.th, styles.numberHeader)}>Rate</th>
              <th className={css(styles.th, styles.numberHeader)}>
                Broker Fee
              </th>
              <th className={css(styles.th, styles.numberHeader)}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const tag = tags.find((t) => String(t.id) === String(item.tag_id))
              const selectedSet = new Set(
                (selectedTagIds || []).filter(
                  (id) => typeof id === 'number' && id > 0,
                ),
              )
              const hasSelection =
                typeof item.tag_id === 'number' && (item.tag_id as number) > 0
              const optionSource = hasSelection ? tags : tagsFiltered
              const rowOptions = optionSource.filter((t) => {
                const id = Number(t.id)
                // Keep this row's current selection, otherwise exclude already-selected tags
                return id === item.tag_id || !selectedSet.has(id)
              })
              const formattedDate = item.date
                ? new Date(item.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  })
                : undefined
              return (
                <tr key={item.id}>
                  <td className={css(styles.td, styles.tagCell)}>
                    {isEditable ? (
                      <select
                        value={String(item.tag_id ?? -1)}
                        onChange={(e) =>
                          onObjectSelect?.(index, Number(e.target.value))
                        }
                        className={css(styles.select)}
                      >
                        <option value={String(-1)}>Select a tag</option>
                        {rowOptions.map((tag) => (
                          <option key={tag.id} value={String(tag.id)}>
                            {tag.label}
                          </option>
                        ))}
                      </select>
                    ) : tag ? (
                      tag.label
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.jobSiteCell)}>
                    {isEditable ? (
                      <input
                        type="text"
                        value={item.job_site || ''}
                        onChange={(e) =>
                          onItemUpdate?.(index, 'job_site', e.target.value)
                        }
                        className={css(styles.tableInput)}
                        placeholder="Job Site"
                      />
                    ) : (
                      item.job_site || '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.truckNumberCell)}>
                    {isEditable ? (
                      <input
                        type="text"
                        value={item.truck_number || ''}
                        onChange={(e) =>
                          onItemUpdate?.(index, 'truck_number', e.target.value)
                        }
                        className={css(styles.tableInput)}
                        placeholder="Truck #"
                      />
                    ) : (
                      item.truck_number || '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.hoursCell)}>
                    {isEditable ? (
                      <input
                        type="number"
                        value={item.hours || ''}
                        onChange={(e) =>
                          onItemUpdate?.(index, 'hours', e.target.value)
                        }
                        className={css(styles.tableInput)}
                        placeholder="Hours"
                      />
                    ) : item.hours != null ? (
                      item.hours
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.loadsCell)}>
                    {isEditable ? (
                      <input
                        type="text"
                        value={item.number_of_loads || ''}
                        readOnly
                        className={css(styles.tableInput)}
                        placeholder="Loads"
                      />
                    ) : (
                      item.number_of_loads || '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.dateCell)}>
                    {isEditable ? (
                      <input
                        type="date"
                        value={
                          item.date
                            ? new Date(item.date).toISOString().split('T')[0]
                            : ''
                        }
                        onChange={(e) =>
                          onItemUpdate?.(index, 'date', e.target.value)
                        }
                        className={css(styles.tableInput)}
                      />
                    ) : (
                      formattedDate || '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.brokerContractorCell)}>
                    {isDriver
                      ? brokerNames[item.tag_id] || '-'
                      : contractorNames[item.tag_id] || '-'}
                  </td>
                  <td className={css(styles.td, styles.materialCell)}>
                    {isEditable ? (
                      <input
                        type="text"
                        value={item.material || ''}
                        onChange={(e) =>
                          onItemUpdate?.(index, 'material', e.target.value)
                        }
                        className={css(styles.tableInput)}
                        placeholder="Material"
                      />
                    ) : (
                      item.material || '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.numberCell)}>
                    {isEditable ? (
                      <input
                        type="number"
                        value={item.volume || ''}
                        onChange={(e) =>
                          onItemUpdate?.(index, 'volume', e.target.value)
                        }
                        className={css(styles.tableInput)}
                        placeholder="Weight"
                      />
                    ) : item.volume != null ? (
                      item.volume
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.numberCell)}>
                    {isEditable ? (
                      <input
                        type="number"
                        value={item.rate || ''}
                        onChange={(e) =>
                          onItemUpdate?.(index, 'rate', e.target.value)
                        }
                        className={css(styles.tableInput)}
                        placeholder="Rate"
                      />
                    ) : item.rate != null ? (
                      item.rate
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.numberCell)}>
                    {isEditable ? (
                      <input
                        type="number"
                        value={item.broker_fee ?? 0}
                        onChange={(e) =>
                          onItemUpdate?.(index, 'broker_fee', e.target.value)
                        }
                        className={css(styles.tableInput)}
                        placeholder="Broker Fee"
                      />
                    ) : item.broker_fee != null ? (
                      item.broker_fee
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className={css(styles.td, styles.numberCell)}>
                    {isEditable ? (
                      <input
                        type="number"
                        value={item.total || ''}
                        onChange={(e) =>
                          onItemUpdate?.(index, 'total', e.target.value)
                        }
                        className={css(styles.tableInput)}
                        placeholder="Total"
                      />
                    ) : (
                      <span className={css(styles.amountText)}>
                        ${calculateItemTotal(item).toFixed(2)}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={11} className={css(styles.td, styles.totalLabel)}>
                Total
              </td>
              <td
                className={css(
                  styles.td,
                  styles.numberCell,
                  styles.totalAmount,
                )}
              >
                ${calculateTotal().toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {isEditable && (
        <div className={css(styles.tableFooter)}>
          <button onClick={onAddItem} className={css(styles.addButton)}>
            <i className="fa-solid fa-plus" /> Add Item
          </button>
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative' as const,
    width: '100%',
    marginBottom: '24px',
  },
  scrollIndicatorLeft: {
    position: 'absolute' as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: 40,
    background:
      'linear-gradient(to right, rgba(255,255,255,0.95), transparent)',
    pointerEvents: 'none' as const,
    zIndex: 1,
  },
  scrollIndicatorRight: {
    position: 'absolute' as const,
    right: 0,
    top: 0,
    bottom: 0,
    width: 40,
    background: 'linear-gradient(to left, rgba(255,255,255,0.95), transparent)',
    pointerEvents: 'none' as const,
    zIndex: 1,
  },
  scrollArrowLeft: {
    position: 'absolute' as const,
    left: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
    },
  },
  scrollArrowRight: {
    position: 'absolute' as const,
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
    },
  },
  itemsTable: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
    marginBottom: '16px',
    fontSize: '12px',
    minWidth: 900,
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #E5E7EB',
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #E5E7EB',
    fontSize: '12px',
  },
  numberHeader: {
    width: '8%',
  },
  numberCell: {
    width: '8%',
  },
  tagHeader: {
    width: '10%',
  },
  tagCell: {
    width: '10%',
  },
  hoursHeader: {
    width: '7%',
  },
  hoursCell: {
    width: '7%',
  },
  tableInput: {
    width: '100%',
    border: '1px solid transparent',
    borderRadius: '4px',
    fontSize: '12px',
    '::placeholder': {
      fontSize: '11px',
      color: '#9CA3AF',
    },
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      backgroundColor: '#F9FAFB',
    },
  },
  tableFooter: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '16px',
  },
  addButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    height: 'fit-content',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  jobSiteHeader: {
    width: '12%',
  },
  jobSiteCell: {
    width: '12%',
  },
  truckNumberHeader: {
    width: '8%',
  },
  truckNumberCell: {
    width: '8%',
  },
  amountText: {
    fontSize: '12px',
    color: '#374151',
  },
  select: {
    width: '100%',
    border: '1px solid transparent',
    borderRadius: '4px',
    fontSize: '12px',
    backgroundColor: 'white',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      backgroundColor: '#F9FAFB',
    },
  },
  totalLabel: {
    textAlign: 'right',
    fontWeight: 600,
    color: '#111827',
    fontSize: '18px',
  },
  totalAmount: {
    fontWeight: 600,
    color: '#111827',
    fontSize: '18px',
  },
  dateHeader: {
    textAlign: 'center',
    width: '9%',
  },
  dateCell: {
    width: '9%',
    textAlign: 'center',
  },
  loadsHeader: {
    width: '7%',
    textAlign: 'center',
  },
  loadsCell: {
    width: '7%',
    textAlign: 'center',
  },
  brokerContractorHeader: {
    width: '10%',
  },
  brokerContractorCell: {
    width: '10%',
  },
  materialHeader: {
    width: '10%',
  },
  materialCell: {
    width: '10%',
  },
})

export default DriverInvoiceItemsTable
